import { Controller } from '@hotwired/stimulus';
import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  size,
} from '@floating-ui/dom';
import {
  clickOutside,
  elementSize,
} from '@inked/utilities';

export class PopoverController extends Controller {
  static targets = [ 'arrow', 'content', 'item', 'reference', 'trigger' ];
  static values = {
    ignoreReference: Boolean,
    offset: Number,
    placement: String,
  };

  activateItem(event) {
    if (!this.isSelectable(event.currentTarget)) return;

    this.setActiveItem(event.currentTarget);
  }

  clearActiveItem() {
    this.selectableItems.forEach(item => {
      item.removeAttribute('data-active');
    });
  }

  hide(event) {
    if (event && this.triggerTarget.contains(event.target)) event.preventDefault();

    this.stopAutoPosition();
    this.stopClickOutside();

    this.clearActiveItem();
    this.contentTarget.style.display = '';
    this.triggerTarget.setAttribute('aria-expanded', false);
    this.triggerTarget.setAttribute('aria-activedescendant', '');
  }

  hotkeys(event) {
    if (!this.isOpen) {
      return;
    }

    if (event.key === 'Escape') {
      this.hide();

      this.triggerTarget.focus();

      return;
    }

    if (!this.selectableItems.length > 0) {
      return;
    }

    if (
      event.shiftKey && event.key === 'Tab'
      || event.key === 'Tab'
    ) {
      this.hide();

      return;
    }

    const index = this.selectableItems.indexOf(this.activeItem);

    if (event.key === 'Enter') {
      event.preventDefault();

      this.activeItem.click();

      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();

      if (index >= this.selectableItems.length - 1) {
        this.setActiveItem(this.selectableItems[0]);

        return;
      }

      this.setActiveItem(this.selectableItems[index + 1]);

      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();

      if (index === 0) {
        this.setActiveItem(this.selectableItems[this.selectableItems.length - 1]);

        return;
      }

      this.setActiveItem(this.selectableItems[index - 1]);
    }
  }

  itemTargetConnected() {
    if (!this.isOpen) return;
    if (this.activeItem) return;

    this.setActiveItem(this.selectableItems[0]);
  }

  isSelectable(item) {
    return !item.disabled;
  }

  setActiveItem(element) {
    this.clearActiveItem();

    element.setAttribute('data-active', true);
    this.triggerTarget.setAttribute('aria-activedescendant', element.id);

    element.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }

  setArrowPosition(x, y) {
    const staticSide = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[this.placementValue.split('-')[0]];

    Object.assign(this.arrowTarget.style, {
      left: x != null ? `${x}px` : '',
      top: y != null ? `${y}px` : '',
      right: '',
      bottom: '',
      [staticSide]:
        [ 'left', 'right' ].includes(staticSide)
          ? `-${elementSize(this.arrowTarget).height * 2}px`
          : `-${elementSize(this.arrowTarget, true).height}`,
    });
  }

  setContentPosition(x, y) {
    Object.assign(this.contentTarget.style, {
      left: `${x}px`,
      top: `${y}px`,
    });
  }

  setExposedWidths() {
    if (this.hasReferenceTarget) {
      this.contentTarget.style.setProperty('--reference-width', elementSize(this.referenceTarget, true).width);
    }

    this.contentTarget.style.setProperty('--trigger-width', elementSize(this.triggerElement, true).width);
  }

  async setPosition() {
    const {
      middlewareData: {
        arrow: {
          x: arrowX,
          y: arrowY,
        } = {},
      },
      placement,
      x,
      y,
    } = await computePosition(
      this.referenceElement,
      this.contentTarget, {
        middleware: [
          offset(this.offsetValue),
          flip(),
          shift({ padding: this.offsetValue }),
          size({
            apply: ({ availableHeight }) => {
              Object.assign(this.contentTarget.style, {
                maxHeight: `${availableHeight - (this.offsetValue * 4)}px`,
              });
            },
          }),
          ... this.hasArrowTarget ? [
            arrow({
              element: this.arrowTarget,
              padding: this.offsetValue,
            })
          ] : [],
        ],
        placement: this.hasPlacementValue ? this.placementValue : 'top',
        strategy: 'fixed',
      });

    this.placementValue = placement;

    if (this.hasArrowTarget) {
      this.setArrowPosition(arrowX, arrowY);
    }

    this.setContentPosition(x, y);
  }

  show(event) {
    if (event && this.triggerTarget.contains(event.target)) event.preventDefault();

    if (this.triggerTarget.hasAttribute('disabled')) {
      return;
    }

    this.stopAutoPosition = autoUpdate(
      this.triggerTarget,
      this.contentTarget,
      () => {
        this.setExposedWidths();
        this.setPosition();
      },
    );

    if (!this.isOpen) {
      this.contentTarget.style.display = 'block';
      this.triggerTarget.setAttribute('aria-expanded', true);

      this.stopClickOutside = clickOutside(this.contentTarget, () => this.hide(), {
        ignore: [
          this.hasReferenceTarget && !this.ignoreReferenceValue ? this.referenceTarget : null,
          this.triggerTarget,
        ],
      });

      if (document.activeElement != this.triggerTarget) {
        this.triggerTarget.focus();
      }

      if (!this.selectableItems.length > 0) return;

      const activeItem =
        this.selectableItems.find(item => item.getAttribute('aria-selected'))
        ?? this.selectableItems[0];

      if (!activeItem) return;

      this.setActiveItem(activeItem);
    }
  }

  toggle(event) {
    if (this.isOpen) {
      this.hide(event);

      return;
    }

    this.show(event);
  }

  get activeItem() {
    if (!this.triggerTarget.getAttribute('aria-activedescendant')) return null;

    return document.querySelector(`#${this.triggerTarget.getAttribute('aria-activedescendant')}`);
  }

  get isOpen() {
    return this.triggerTarget.getAttribute('aria-expanded') === 'true';
  }

  get referenceElement() {
    if (this.hasReferenceTarget) return this.referenceTarget;

    return this.triggerElement;
  }

  get selectableItems() {
    if (!this.hasItemTarget) return [];

    return this.itemTargets.filter(item => this.isSelectable(item));
  }

  get triggerElement() {
    return this.triggerTarget.checkVisibility()
      ? this.triggerTarget
      : [...this.triggerTarget.children].find(child => child.checkVisibility());
  }
}
